import type { PricedLine } from '@agos/customer-core'

/**
 * The checkout's order list, with box lines gathered under the box they came from — §9.166.
 *
 * **The owner's report:** *"when using burger box … I go to vaša objednávka, I see all of the things
 * separately"*. The basket already knows better: a box's lines carry `boxGroupId`, `boxGroupName` and
 * `boxSlotLabel`, `removeBox` deletes them together, and the menu screen draws them as one card. Only
 * the checkout had never read those fields, so a two-burger box arrived as four unrelated rows with
 * four steppers, three of which could take the box apart.
 *
 * **Order is preserved and the group lands where its first line was.** Legacy does the same, and it
 * matters: a box added first stays first, so the list still reads in the order things were chosen.
 *
 * **A box is not a quantity.** Its lines are listed with their slot labels and a single total; there
 * is no stepper, because half a box is not something the kitchen makes. Removing it removes all of
 * it, which is what `removeBox` was written for.
 */

export interface SingleEntry {
  kind: 'single'
  entry: PricedLine
  /**
   * §9.713 - the lines ordered with this one (`addonOf` is this entry's key), drawn under it like a box's slots.
   *
   * Empty for every line nothing was ordered with, which is every line before §9.713.
   */
  addons: PricedLine[]
}

export interface BoxEntry {
  kind: 'box'
  groupId: string
  /** Falls back to a generic name: a box saved before the name was stored is still a box. */
  name: string
  items: PricedLine[]
  totalCents: number
}

export type BasketEntry = SingleEntry | BoxEntry

/** What a box with no stored name is called. Legacy's fallback, and for the same reason. */
export const UNNAMED_BOX = 'Burger Box'

export function groupBasket(priced: readonly PricedLine[]): BasketEntry[] {
  const boxes = new Map<string, BoxEntry>()
  const ordered: BasketEntry[] = []

  /* §9.713 - an add-on is drawn under the line it was ordered with, so the fries read as burger Bacon's and not as a
     fourth thing in the basket. Found by key in a first pass, so the order the two were added in does not matter; an
     add-on whose parent is not a plain line here (gone, or in a box) is drawn on its own row, as it was before. */
  const parents = new Map<string, SingleEntry>()
  for (const entry of priced) {
    if (entry.line.boxGroupId === undefined && entry.line.addonOf === undefined) {
      parents.set(entry.key, { kind: 'single', entry, addons: [] })
    }
  }

  for (const entry of priced) {
    const groupId = entry.line.boxGroupId
    const parent = entry.line.addonOf === undefined ? undefined : parents.get(entry.line.addonOf)
    if (parent !== undefined) {
      parent.addons.push(entry)
      continue
    }
    if (groupId === undefined) {
      ordered.push(parents.get(entry.key) ?? { kind: 'single', entry, addons: [] })
      continue
    }
    const existing = boxes.get(groupId)
    if (existing === undefined) {
      // The group takes the position of its first line, so the list keeps the order things were
      // added in. Pushing groups at the end would reorder a basket the customer built deliberately.
      const box: BoxEntry = {
        kind: 'box',
        groupId,
        name: entry.line.boxGroupName ?? UNNAMED_BOX,
        items: [entry],
        totalCents: entry.totalCents,
      }
      boxes.set(groupId, box)
      ordered.push(box)
      continue
    }
    existing.items.push(entry)
    existing.totalCents += entry.totalCents
  }

  return ordered
}
